import { useMemo } from "react"
import { useAuth } from "@/lib/auth/auth-context"
import { useOrganization } from "@/lib/organization/organization-context"
import {
  useTeamMembers,
  useTeamInvites,
  useConnectedBodyshop,
  useTeam,
  useGroupDealerships,
} from "@/lib/api/use-teams"
import { feesNotReviewed } from "@/lib/fees/defaults"

// Setup-task warnings are derived from the same TanStack Query data the rest
// of the app already loads — there is no separate "skipped" persistence. A
// task shows whenever onboarding is complete (the user has a team) AND its
// underlying condition is unmet. As soon as the condition is satisfied the
// task disappears on its own.
export type SetupTaskId =
  | "invite_members"
  | "invite_group_leadership"
  | "connect_bodyshop"
  | "setup_fees"
  | "add_locations"

export type SetupTaskTone = "neutral" | "amber"

export interface SetupTask {
  id: SetupTaskId
  title: string
  description: string
  to: string
  cta: string
  // `amber` is reserved for warnings whose absence would let the user produce
  // wrong/undeliverable output (placeholder fees → wrong numbers; missing body
  // shop → undeliverable estimate). The estimate gate also fires for these.
  tone: SetupTaskTone
}

const TASK_DEFS: Record<SetupTaskId, SetupTask> = {
  invite_members: {
    id: "invite_members",
    title: "Invite your team",
    description: "You're the only one here. Send invites so advisors and managers can see estimates as they come in.",
    to: "/settings?tab=team",
    cta: "Send invites",
    tone: "neutral",
  },
  invite_group_leadership: {
    id: "invite_group_leadership",
    title: "Invite your directors and GMs",
    description: "Your group team is just you right now. Add the directors and general managers who'll see all dealerships at a glance.",
    to: "/settings?tab=team",
    cta: "Send invites",
    tone: "neutral",
  },
  connect_bodyshop: {
    id: "connect_bodyshop",
    title: "Connect a body shop",
    description: "Estimates need a partner shop to receive them. Without one, you can't send any.",
    to: "/settings?tab=bodyshops",
    cta: "Connect shop",
    tone: "amber",
  },
  setup_fees: {
    id: "setup_fees",
    title: "Set your shop rates",
    description: "Estimates will price wrong until you set your real rates — placeholder defaults aren't safe to send.",
    to: "/settings?tab=fees",
    cta: "Set rates",
    tone: "amber",
  },
  add_locations: {
    id: "add_locations",
    title: "Add dealership locations",
    description: "Each location gets its own team, estimates, and reporting.",
    to: "/settings?tab=dealerships",
    cta: "Add locations",
    tone: "neutral",
  },
}

export interface SetupTasksResult {
  isLoading: boolean
  pending: SetupTask[]
  hasPending: boolean
  // Convenience: any amber-tone task pending? Drives the sidebar dot's amber
  // treatment to mirror the gate severity.
  hasBlocking: boolean
}

export function useSetupTasks(): SetupTasksResult {
  const { hasTeams } = useAuth()
  const {
    activeTeamKind,
    activeTeamId,
    activeDealershipId,
    activeGroupId,
    availableGroups,
    isLoading: orgLoading,
  } = useOrganization()

  const isGroupOwner = availableGroups.length > 0
  const groupIdNum = activeGroupId ? Number(activeGroupId) : null
  const teamIdNum = activeTeamId ? Number(activeTeamId) : null

  const groupMembersQuery = useTeamMembers(isGroupOwner ? groupIdNum : null)
  const groupInvitesQuery = useTeamInvites(isGroupOwner ? groupIdNum : null)
  const groupDealershipsQuery = useGroupDealerships(isGroupOwner ? activeGroupId : null)

  const membersQuery = useTeamMembers(!isGroupOwner ? teamIdNum : null)
  const invitesQuery = useTeamInvites(!isGroupOwner ? teamIdNum : null)
  const bodyshopQuery = useConnectedBodyshop(
    !isGroupOwner && activeTeamKind === "dealership" ? activeDealershipId : null,
  )
  const teamQuery = useTeam(
    !isGroupOwner && activeTeamKind === "bodyshop" ? activeTeamId : null,
  )

  // The banner / sidebar dot must NEVER flash "no warnings" while we're
  // still resolving the user's teams or which team is active. Without this
  // gate, a freshly signed-up dealership lands on the dashboard with the
  // gate-relevant warning briefly hidden, then it pops in once the bodyshop
  // query resolves — a flicker that reads as "the warning randomly comes
  // and goes." Stay loading until the relevant query has actually returned.
  const isLoading =
    orgLoading ||
    (isGroupOwner
      ? !groupIdNum ||
        groupMembersQuery.isLoading ||
        groupInvitesQuery.isLoading ||
        groupDealershipsQuery.isLoading
      : hasTeams &&
        (!teamIdNum ||
          membersQuery.isLoading ||
          invitesQuery.isLoading ||
          (activeTeamKind === "dealership" && bodyshopQuery.isLoading) ||
          (activeTeamKind === "bodyshop" && teamQuery.isLoading)))

  const pending = useMemo<SetupTask[]>(() => {
    if (!hasTeams) return []
    const tasks: SetupTask[] = []

    if (isGroupOwner) {
      if (!groupIdNum) return []
      const memberCount = groupMembersQuery.data?.length ?? 0
      const inviteCount = groupInvitesQuery.data?.length ?? 0
      if (memberCount <= 1 && inviteCount === 0) {
        tasks.push(TASK_DEFS.invite_group_leadership)
      }
      if (
        !groupDealershipsQuery.isLoading &&
        (groupDealershipsQuery.data?.length ?? 0) === 0
      ) {
        tasks.push(TASK_DEFS.add_locations)
      }
      return tasks
    }

    if (!teamIdNum) return []

    const memberCount = membersQuery.data?.length ?? 0
    const inviteCount = invitesQuery.data?.length ?? 0
    if (memberCount <= 1 && inviteCount === 0) {
      tasks.push(TASK_DEFS.invite_members)
    }

    if (activeTeamKind === "dealership") {
      if (!bodyshopQuery.isLoading && !bodyshopQuery.data) {
        tasks.push(TASK_DEFS.connect_bodyshop)
      }
    }

    if (activeTeamKind === "bodyshop") {
      if (teamQuery.data && feesNotReviewed(teamQuery.data.metadata)) {
        tasks.push(TASK_DEFS.setup_fees)
      }
    }

    return tasks
  }, [
    hasTeams,
    isGroupOwner,
    groupIdNum,
    groupMembersQuery.data,
    groupInvitesQuery.data,
    groupDealershipsQuery.data,
    groupDealershipsQuery.isLoading,
    teamIdNum,
    membersQuery.data,
    invitesQuery.data,
    activeTeamKind,
    bodyshopQuery.data,
    bodyshopQuery.isLoading,
    teamQuery.data,
  ])

  const hasBlocking = pending.some((t) => t.tone === "amber")

  return { isLoading, pending, hasPending: pending.length > 0, hasBlocking }
}
